import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import LocationTypeIcon from '#/shared/components/LocationTypeIcon';
import {
  CURRENT_LOCATION_TYPE,
  DESTINATION_LOCATION_TYPE,
  ORIGIN_LOCATION_TYPE,
} from '#/shared/constants';
import { locationType as locationPropType } from '#/prop-types';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import * as Styled from './styled';

function EndpointField({
  location,
  locationType,
  userLocation,
  onClick,
  gtmHandle,
  labelIntlId,
  emptyInputIntlId,
  accessibilityIntlId,
}) {
  const checkUserAtLocation = useCallback(
    () =>
      !!(
        location &&
        userLocation &&
        // round lat, lon to 4 decimal places to give a tolerance of ~10
        // meters when matching the user location to the current location
        // https://gis.stackexchange.com/a/8674/21718
        ((location.lon.toFixed(4) === userLocation.lon.toFixed(4) &&
          location.lat.toFixed(4) === userLocation.lat.toFixed(4)) ||
          // if the coordinates aren't equivalent check if the reverse
          // geocoded names match
          (location.name &&
            userLocation.name &&
            location.name === userLocation.name))
      ),
    [location, userLocation]
  );

  const [userAtLocation, setUserAtLocation] = useState(() =>
    checkUserAtLocation()
  );

  useEffect(() => {
    setUserAtLocation(checkUserAtLocation());
  }, [checkUserAtLocation]);

  const intl = useIntl();

  return (
    <Styled.EndpointButton onClick={onClick} dataGtm={gtmHandle}>
      <Styled.EndpointIconColumn>
        <LocationTypeIcon
          locationType={userAtLocation ? CURRENT_LOCATION_TYPE : locationType}
        />
      </Styled.EndpointIconColumn>
      <Styled.EndpointContentColumn>
        <Styled.EndpointLabel>
          {`${intl.formatMessage({ id: labelIntlId })}${
            userAtLocation
              ? ` (${intl.formatMessage({
                  id: 'planner.current-location-label',
                })})`
              : ''
          }`}
        </Styled.EndpointLabel>
        <Styled.EndpointInput isFaded={!!location}>
          <Styled.EndpointText>
            {location
              ? location.name
              : intl.formatMessage({ id: emptyInputIntlId })}
          </Styled.EndpointText>
        </Styled.EndpointInput>
        <ScreenReaderText>
          {intl.formatMessage({ id: accessibilityIntlId })}
        </ScreenReaderText>
      </Styled.EndpointContentColumn>
    </Styled.EndpointButton>
  );
}

EndpointField.propTypes = {
  /** Trip end point location; name is displayed in input element */
  location: locationPropType,
  /** Type of location being supplied */
  locationType: PropTypes.oneOf([
    ORIGIN_LOCATION_TYPE,
    DESTINATION_LOCATION_TYPE,
  ]).isRequired,
  /** The user's current location */
  userLocation: locationPropType,
  /** Function executed when field is clicked */
  onClick: PropTypes.func.isRequired,
  /** Handle for Google Tag Manager; applied to top level element */
  gtmHandle: PropTypes.string.isRequired,
  /** Localization ID for label text */
  labelIntlId: PropTypes.string.isRequired,
  /** Localization ID for text in input element when empty */
  emptyInputIntlId: PropTypes.string.isRequired,
  /** Localization ID for element accessibility attribute text */
  accessibilityIntlId: PropTypes.string.isRequired,
};

EndpointField.defaultProps = {
  location: null,
  userLocation: null,
};

export default EndpointField;
